import React from 'react'
import {useSelector} from 'react-redux'
import {DateGuestInput} from 'components/Accommodation/DateGuestInput'
import {scrollToSection} from 'components/ScrollSpy'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {isVioOfferOrBundleSelected} from 'modules/accommodation/utils'
import {DateGuestPickerSource} from 'modules/overlay/types'
import {
  getOptimizedRoomsConfig,
  getSearchParameters
} from 'modules/sapiSearch/selectors'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {Text} from '@vio/atlas/Text'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'

import {useIsRoomsListImprovementsB} from '../hooks/useIsRoomsListImprovementsB'
import {RoomsSectionSearchContextGuestCount} from './RoomsSectionSearchContextGuestCount'
import {RoomsSectionSearchContextSubtitle} from './RoomsSectionSearchContextSubtitle'

const handleChangeButtonClick = () => {
  scrollToSection(AccommodationPageNavSections.ReviewsSummary)
}

export const RoomsSectionSearchInputContext = ({
  hotelId
}: {
  hotelId: string
}) => {
  const {isMobile} = useDeviceLayout()
  const isRoomsListImprovementsB = useIsRoomsListImprovementsB()
  const isVioOfferSelected = isVioOfferOrBundleSelected()
  const searchParameters = useSelector(getSearchParameters)
  const {checkIn, checkOut} = searchParameters || {} // They may not be synced yet
  const roomsString = useSelector(state =>
    getOptimizedRoomsConfig(state, hotelId)
  )
  const {numberOfAdults, numberOfChildren, numberOfRooms} =
    roomsToConfigurationObject(roomsString)

  const isLoading = !checkIn || !checkOut || !roomsString

  if (!isMobile && !isRoomsListImprovementsB) {
    return null
  }

  if (isMobile && isVioOfferSelected) {
    return (
      <Box paddingTop="s300">
        <DateGuestInput source={DateGuestPickerSource.HotelViewRoomsSection} />
      </Box>
    )
  }

  if (isLoading) {
    return <SkeletonLoader width={100} />
  }

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <div data-id="searchContext">
        {isRoomsListImprovementsB ? (
          <RoomsSectionSearchContextSubtitle
            checkIn={checkIn}
            checkOut={checkOut}
            numberOfRooms={numberOfRooms}
            numberOfAdults={numberOfAdults}
            numberOfChildren={numberOfChildren}
          />
        ) : (
          <RoomsSectionSearchContextGuestCount
            checkIn={checkIn}
            checkOut={checkOut}
            numberOfGuests={numberOfAdults + numberOfChildren}
            numberOfRooms={numberOfRooms}
          />
        )}
      </div>
      {isMobile && (
        <ButtonLink
          onClick={handleChangeButtonClick}
          size="md"
          variant={isRoomsListImprovementsB ? 'primary' : 'secondary'}
          isCompact
        >
          <Text variant="labelS">
            <FormattedMessageWrapper
              id="roomsList.change"
              defaultMessage="Change"
            />
          </Text>
        </ButtonLink>
      )}
    </Box>
  )
}
